import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import {
  invalidateAllPlanQueries,
  invalidateCertificateQueries,
} from '@/utils/queryInvalidation';

import Constants from '@/constants';

import {
  Plan,
  PlanUpdateRequest,
  Section,
  Step,
  updatePlanMutation,
} from '@/_generated/api-clients/xp';

const buildPayload = (
  sourcePlan: Plan,
  updates: Partial<PlanUpdateRequest>,
): PlanUpdateRequest => {
  const effectiveAutoAssign = updates.autoAssign ?? sourcePlan.autoAssign;

  const sourceStepIds = new Set(
    [
      ...(sourcePlan.content?.steps ?? []),
      ...(sourcePlan.content?.sections?.flatMap(s => s.steps ?? []) ?? []),
    ].flatMap(s => (s.id ? [s.id] : [])),
  );

  const validateStep = (step: {
    type?: string;
    reference?: string | null;
    id?: string;
  }) => {
    if (
      step.type === Constants.STEP_TYPES.DOCUMENT &&
      !step.reference &&
      !(step.id && sourceStepIds.has(step.id))
    ) {
      const err = new Error(
        'CRITICAL: Document step reached API payload without reference - upstream validation failed',
        { cause: step },
      );
      datadogRum.addError(err);
      throw err;
    }
  };

  const sections = updates.content?.sections ?? sourcePlan.content?.sections;
  const steps = updates.content?.steps ?? sourcePlan.content?.steps;

  steps?.forEach(validateStep);
  sections?.forEach(s => s.steps?.forEach(validateStep));

  // PlanStep (GET response) carries resource.id; Step (PUT request) needs
  // that value in the reference field. Strip resource to avoid sending the
  // enriched object back to the API.
  const normalizeStep = (step: Step): Step => {
    const { resource, ...rest } = step as Step & {
      resource?: { id?: string };
    };
    return { ...rest, reference: rest.reference ?? resource?.id };
  };

  const normalizedContent =
    sections !== undefined || steps !== undefined
      ? {
          content: {
            sections: sections?.map(s => ({
              ...s,
              steps: s.steps?.map(normalizeStep),
            })),
            steps: steps?.map(normalizeStep),
          },
        }
      : {};

  return {
    ...sourcePlan,
    ...updates,
    dueDateOffsetInDays: effectiveAutoAssign
      ? (updates.dueDateOffsetInDays ?? sourcePlan.dueDateOffsetInDays ?? 14)
      : undefined,
    ...normalizedContent,
  };
};

interface UseUpdatePlanOptions {
  onSuccess?: () => void | Promise<void>;
}

/**
 * Hook for plan mutations. Accepts Plan and PlanUpdateRequest directly — no legacy shape normalization.
 *
 * @example
 * const { addSteps, updateName, isPending } = useUpdatePlan();
 * await addSteps(plan, newSteps, sectionId);
 */
export const useUpdatePlan = (options?: UseUpdatePlanOptions) => {
  const baseMutation = updatePlanMutation();

  const mutation = useMutation({
    mutationKey: baseMutation.mutationKey,
    mutationFn: baseMutation.mutationFn,
    onSuccess: async (_data, variables) => {
      await Promise.all([
        invalidateAllPlanQueries(variables.path.planId),
        invalidateCertificateQueries(variables.path.planId),
      ]);
      await options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      datadogRum.addError(error);
    },
  });

  const mutate = async (plan: Plan, updates: Partial<PlanUpdateRequest>) =>
    mutation.mutateAsync({
      path: { planId: plan.id },
      body: buildPayload(plan, updates),
    });

  const addSteps = (plan: Plan, newSteps: Step[], sectionId?: string) => {
    // The incoming step.id may be a resource/document ID which is not unique
    // when the same resource is added to a plan multiple times.
    const stepsWithIds = newSteps.map(step => ({ ...step, id: uuidv4() }));

    return mutate(
      plan,
      sectionId
        ? {
            content: {
              steps: plan.content?.steps,
              sections: plan.content?.sections?.map(section =>
                section.id === sectionId
                  ? {
                      ...section,
                      steps: [...(section.steps ?? []), ...stepsWithIds],
                    }
                  : section,
              ),
            },
          }
        : {
            content: {
              steps: [...(plan.content?.steps ?? []), ...stepsWithIds],
              sections: plan.content?.sections,
            },
          },
    );
  };

  const addSection = (plan: Plan, section: Section) =>
    mutate(plan, {
      content: { sections: [...(plan.content?.sections ?? []), section] },
    });

  const replaceStepResource = (
    plan: Plan,
    stepId: string,
    replacement: { name: Record<string, string>; reference?: string },
    sectionId?: string,
  ) => {
    const patchStep = (step: Step): Step =>
      step.id === stepId ? { ...step, ...replacement } : step;

    return mutate(plan, {
      content: sectionId
        ? {
            steps: plan.content?.steps,
            sections: plan.content?.sections?.map(section =>
              section.id === sectionId
                ? { ...section, steps: section.steps?.map(patchStep) }
                : section,
            ),
          }
        : {
            steps: plan.content?.steps?.map(patchStep),
            sections: plan.content?.sections,
          },
    });
  };

  const updateContent = (plan: Plan, content: PlanUpdateRequest['content']) =>
    mutate(plan, { content });

  const updateName = (plan: Plan, name: PlanUpdateRequest['name']) =>
    mutate(plan, { name });

  const updateTranslations = (
    plan: Plan,
    translations: {
      name?: PlanUpdateRequest['name'];
      sections?: Section[];
      steps?: Step[];
    },
  ) =>
    mutate(plan, {
      name: translations.name ?? plan.name,
      content: { sections: translations.sections, steps: translations.steps },
    });

  const updateEstimatedTime = (
    plan: Plan,
    estimatedTimeToCompleteInMinutes: number,
  ) => mutate(plan, { estimatedTimeToCompleteInMinutes });

  const updateCategory = (plan: Plan, category: string) =>
    mutate(plan, { category });

  const deleteStep = (plan: Plan, stepId: string, sectionId?: string | null) =>
    mutate(plan, {
      content: sectionId
        ? {
            sections: plan.content?.sections?.map(section =>
              section.id === sectionId
                ? {
                    ...section,
                    steps: section.steps?.filter(step => step.id !== stepId),
                  }
                : section,
            ),
          }
        : {
            steps: plan.content?.steps?.filter(step => step.id !== stepId),
          },
    });

  const deleteSection = (plan: Plan, sectionId: string) =>
    mutate(plan, {
      content: {
        sections: plan.content?.sections?.filter(s => s.id !== sectionId),
      },
    });

  const renameSection = (
    plan: Plan,
    sectionId: string,
    name: PlanUpdateRequest['name'],
  ) =>
    mutate(plan, {
      content: {
        sections: plan.content?.sections?.map(section =>
          section.id === sectionId ? { ...section, name } : section,
        ),
      },
    });

  const { isPending, isError, error } = mutation;

  return {
    addSteps,
    addSection,
    deleteStep,
    deleteSection,
    renameSection,
    replaceStepResource,
    updateContent,
    updateCategory,
    updateEstimatedTime,
    updateTranslations,
    updateName,
    isPending,
    isError,
    error,
  };
};
